import classnames from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import './Pagination.scss';
import { usePaginationAndSortParams } from '../hooks/usePaginationAndSortParams';

const DEFAULT_PAGE_RANGE = 5;

type Props = {
    readonly placeholder?: boolean;
    readonly hidden?: boolean;
    readonly className?: string;
    readonly totalItems: number | undefined;
    readonly currentPage?: number;
    readonly pageSize: number;
    readonly onPageChange?: (page: number) => void;
};

export const Pagination: FC<Props> = ({
    totalItems = 0,
    pageSize,
    hidden = false,
    placeholder = false,
    onPageChange,
    currentPage: currentPageOverride,
    className
}) => {
    const { page, setPage } = usePaginationAndSortParams();
    const currentPage = currentPageOverride ?? page;
    const totalPages = Math.ceil(totalItems / pageSize);

    const handlePageChange = useCallback(
        ({ selected }: { readonly selected: number }) => {
            if (onPageChange) {
                onPageChange(selected);
            } else {
                setPage(selected);
            }
        },
        [onPageChange, setPage]
    );

    const handleFirstPage = useCallback(
        (evt: React.MouseEvent) => {
            evt.preventDefault();
            if (onPageChange) {
                onPageChange(0);
            } else {
                setPage(0);
            }
        },
        [onPageChange, setPage]
    );

    const previousPageButton = useMemo(() => {
        if (currentPage <= 0) {
            return null;
        }

        if (DEFAULT_PAGE_RANGE < totalPages && currentPage + 2 >= DEFAULT_PAGE_RANGE) {
            return (
                <>
                    <i className="material-icons">navigate_before</i>
                    <span className="break">&hellip;</span>
                </>
            );
        }

        return <i className="material-icons">navigate_before</i>;
    }, [currentPage, totalPages]);

    const nextPageButton = useMemo(() => {
        if (DEFAULT_PAGE_RANGE < totalPages && currentPage < totalPages - 2) {
            return (
                <>
                    <span className="break">&hellip;</span>
                    <i className="material-icons">navigate_next</i>
                </>
            );
        }

        if (currentPage !== totalPages - 1) {
            return <i className="material-icons">navigate_next</i>;
        }

        return null;
    }, [currentPage, totalPages]);

    if (hidden || totalItems === 0 || totalPages < 2) {
        return placeholder ? <div>&nbsp;</div> : null;
    }

    return (
        <div style={{ clear: 'both' }} className={classnames('gild pagination', className)}>
            {currentPage > 0 ? (
                <ul className="first-page">
                    <li>
                        <a onClick={handleFirstPage}>
                            {/* FIXME fix icon */}
                            <i className="material-icons">first_page</i>
                        </a>
                    </li>
                </ul>
            ) : null}
            <ReactPaginate
                pageCount={totalPages}
                onPageChange={handlePageChange}
                forcePage={currentPage}
                pageRangeDisplayed={DEFAULT_PAGE_RANGE}
                marginPagesDisplayed={0}
                activeClassName="active"
                previousClassName="previous"
                nextClassName="next"
                previousLabel={previousPageButton}
                nextLabel={nextPageButton}
                breakLabel=""
            />
        </div>
    );
};

export default Pagination;
